import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { intakeQuestionnaireActions } from '../../../../../../../store';

import type { AppDispatch, RootState } from '../../../../../../../store';
import type { IntakeReferralPayload } from '../../../../../../../store/questionnaires/intake';

export default function useIntakeReferrals() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { intakeQuestionnaireId } = useParams<'intakeQuestionnaireId'>();
  const referrals = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.intakeQuestionnaireDetails?.referrals ?? [],
  );
  const isSaving = useSelector(
    (state: RootState) =>
      state.intakeQuestionnaire.createReferralLoading ||
      state.intakeQuestionnaire.updateReferralLoading ||
      state.intakeQuestionnaire.deleteReferralLoading,
  );

  const create = useCallback(
    async (data: IntakeReferralPayload) => {
      if (!intakeQuestionnaireId) return undefined;
      const result = await dispatch(
        intakeQuestionnaireActions.createIntakeReferral({
          intakeQuestionnaireId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.createIntakeReferral.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const update = useCallback(
    async (referralId: number, data: IntakeReferralPayload) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.updateIntakeReferral({
          intakeQuestionnaireId,
          referralId,
          data,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.updateIntakeReferral.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  const remove = useCallback(
    async (referralId: number) => {
      if (!intakeQuestionnaireId) return false;
      const result = await dispatch(
        intakeQuestionnaireActions.deleteIntakeReferral({
          intakeQuestionnaireId,
          referralId,
          getAccessTokenSilently,
        }),
      );
      return intakeQuestionnaireActions.deleteIntakeReferral.fulfilled.match(result);
    },
    [dispatch, getAccessTokenSilently, intakeQuestionnaireId],
  );

  return { referrals, isSaving, create, update, remove };
}
